/**
 * Cluster géographique de communes — utilitaire réutilisable.
 * Source unique : geoPages.js (318 entrées avec coordonnées GPS).
 *
 * - haversine() : distance précise entre 2 points GPS (km)
 * - calcCommunePrice() : tarif intelligent depuis Nantes (35€ + 1.8€/km, arrondi 5€)
 * - getNearbyCommunes() : top N communes les plus proches d'un slug donné
 */
import { geoPages } from '../data/geoPages';

const NANTES_LAT = 47.2184;
const NANTES_LNG = -1.5536;

export function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Tarif fixe Goloire-Cab depuis Nantes : 35€ base + 1.8€/km, arrondi 5€, min 40€. */
export function calcCommunePrice(lat, lng) {
  const dist = haversine(NANTES_LAT, NANTES_LNG, lat, lng);
  const raw = 35 + 1.8 * dist;
  return Math.max(40, Math.round(raw / 5) * 5);
}

/** Trajet estimé (60 km/h moyenne) en minutes, arrondi à 5 min. */
export function estimateTravelTimeMin(distanceKm, avgSpeedKmh = 60) {
  const min = (distanceKm / avgSpeedKmh) * 60;
  return Math.max(15, Math.round(min / 5) * 5);
}

/**
 * Retourne les N communes les plus proches d'un slug donné.
 * Utilise une recherche par coordonnées GPS sur geoPages.js (toutes communes).
 * @param {string} slug - slug de la commune actuelle (ex: 'vtc-piriac-sur-mer')
 * @param {number} limit - nombre max de communes (défaut 6)
 * @param {number} maxRadiusKm - rayon max en km (défaut 30, pour éviter les trop éloignées)
 */
export function getNearbyCommunes(slug, limit = 6, maxRadiusKm = 30) {
  const current = geoPages.find((p) => p.slug === slug);
  if (!current?.coordinates?.lat) return [];

  const { lat, lng } = current.coordinates;

  return geoPages
    .filter(
      (p) =>
        p.slug !== slug &&
        p.coordinates?.lat &&
        p.coordinates?.lng,
    )
    .map((p) => {
      const distKm = haversine(lat, lng, p.coordinates.lat, p.coordinates.lng);
      return {
        slug: p.slug,
        city: p.city,
        distance: distKm,
        durationMin: estimateTravelTimeMin(distKm),
        price: calcCommunePrice(p.coordinates.lat, p.coordinates.lng),
      };
    })
    .filter((p) => p.distance <= maxRadiusKm)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
}
